import { classes } from 'html-classes';
import { isEqual } from 'lodash-es';
import { forwardRef, useEffect, useRef } from 'react';

import { company } from '~/company/Company';
import { IconButton } from '~/components/isolated/IconButton';
import { useGlobal } from '~/hooks/useGlobal';

import {
  ON_PRODUCT_CLICK_EVENT,
  SOMMELIER_BOT_ELEMENT_IDENTIFIER,
} from './constants';
import { SommelierWindowProps } from './interfaces';
import {
  adjustSommelierBotElement,
  isScriptExists,
  renderScriptElement,
} from './utils';

const SommelierWindow = forwardRef<HTMLDivElement, SommelierWindowProps>(
  ({ open = false, onProductClick, className, style, onClose }, ref) => {
    const { isSSR } = useGlobal();

    const innerRef = useRef<HTMLDivElement | null>(null);
    const mutationObserverRef = useRef<MutationObserver | null>(null);

    const handleMountElement = async (node: HTMLDivElement | null) => {
      if (
        !node ||
        isSSR ||
        (isEqual(node, innerRef.current) && isScriptExists(node))
      ) {
        return;
      }

      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
      }

      innerRef.current = node;

      const mutationObserver = new MutationObserver((mutations) => {
        const sommelierElementAdded = mutations.some((item) =>
          Array.from(item.addedNodes).some((node) => {
            const element = node as Nullable<HTMLElement | undefined>;

            if (typeof element?.getAttribute !== 'function') {
              return false;
            }

            return (
              (element as HTMLElement).getAttribute('id') ===
              SOMMELIER_BOT_ELEMENT_IDENTIFIER
            );
          }),
        );

        if (sommelierElementAdded) {
          adjustSommelierBotElement();
        }
      });
      mutationObserver.observe(node, { childList: true, subtree: true });
      mutationObserverRef.current = mutationObserver;
      await renderScriptElement(node);
    };

    useEffect(() => {
      const listener = (e: Event) => {
        const { detail } = e as CustomEvent;

        onProductClick?.(detail, e);
      };

      window.addEventListener(ON_PRODUCT_CLICK_EVENT, listener);

      return () => {
        window.removeEventListener(ON_PRODUCT_CLICK_EVENT, listener);
        // Отключаем MutationObserver при размонтировании компонента
        if (mutationObserverRef.current) {
          mutationObserverRef.current.disconnect();
          mutationObserverRef.current = null;
        }
      };
    }, []);

    const handleClose = () => {
      onClose?.();
    };

    useEffect(() => {
      if (isSSR) {
        return;
      }

      const appElement = document.getElementById('app');

      if (!appElement) {
        return;
      }

      if (open) {
        appElement.classList.add('app-no-scroll');
      } else {
        appElement.classList.remove('app-no-scroll');
      }

      return () => {
        appElement.classList.remove('app-no-scroll');
      };
    }, [open, isSSR]);

    return (
      <div
        ref={ref}
        data-company={company.name}
        className={classes([className, open && 'opened'])}
        style={style}
      >
        <IconButton
          size="S"
          variant="outline"
          icon="close"
          className="close-button"
          onClick={handleClose}
        />
        <div className="inner" ref={handleMountElement} />
      </div>
    );
  },
);

SommelierWindow.displayName = 'SommelierWindow';

export default SommelierWindow;
